import React, { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  IconButton,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Typography,
  Button,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ExpandMore,
  ExpandLess,
  Add,
  MoreVert,
  Flag,
  Person,
  LockOutlined,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import { Task, Epic, TaskStatus, TaskPriority, TaskType } from '../../types/common';
import { Sprint } from '../../entities/sprint/model/types';
import { useBoardMembers } from '../../hooks/useBoardMembers';
import TaskDetail from '../Tasks/TaskDetail';
import './TableBoardView.scss';

interface TableBoardViewProps {
  tasks: Task[];
  sprints: Sprint[];
  epics: Epic[];
  onUpdateTask: (taskId: string, updates: Partial<Task>) => void;
  onCreateTask: (sprintId?: string) => void;
  groupBy: 'sprint' | 'epic' | 'status' | 'none';
  canCreateTask?: boolean;
  boardId?: string;
}

const TableBoardView: React.FC<TableBoardViewProps> = ({
  tasks,
  sprints,
  epics,
  onUpdateTask,
  onCreateTask,
  groupBy = 'sprint',
  canCreateTask = true,
  boardId,
}) => {
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [dragOverGroupId, setDragOverGroupId] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const effectiveBoardId = boardId || (tasks[0]?.boardId ? String(tasks[0].boardId) : sprints[0]?.boardId ? String(sprints[0].boardId) : undefined);
  const { members: boardMembers } = useBoardMembers(effectiveBoardId);

  // Group tasks based on groupBy prop
  const groupedTasks = useMemo(() => {
    const groups: { [key: string]: Task[] } = {};

    if (groupBy === 'sprint') {
      // Add backlog first so it appears at the top
      groups['backlog'] = tasks.filter((task) => !task.sprintId);

      // Group by sprint
      sprints.forEach((sprint) => {
        const sId = String(sprint.id);
        groups[sId] = tasks.filter((task) => String(task.sprintId) === sId);
      });
    } else if (groupBy === 'epic') {
      // Group by epic
      epics.forEach((epic) => {
        const eId = String(epic.id);
        groups[eId] = tasks.filter((task) => String(task.epicId) === eId);
      });
      // Add tasks without epic
      groups['no-epic'] = tasks.filter((task) => !task.epicId);
    } else if (groupBy === 'status') {
      // Group by status
      Object.values(TaskStatus).forEach((status) => {
        groups[status] = tasks.filter((task) => task.status === status);
      });
    } else {
      // No grouping
      groups['all'] = tasks;
    }

    return groups;
  }, [tasks, sprints, epics, groupBy]);

  const toggleGroup = (groupId: string) => {
    setCollapsedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const handleDragStart = (e: React.DragEvent, taskId: string) => {
    e.dataTransfer.setData('text/plain', taskId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedTaskId(taskId);
  };

  const handleDragEnd = () => {
    setDraggedTaskId(null);
    setDragOverGroupId(null);
  };

  const handleDragOver = (e: React.DragEvent, groupId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverGroupId !== groupId) {
      setDragOverGroupId(groupId);
    }
  };

  const handleDragLeave = (e: React.DragEvent, groupId: string) => {
    if (dragOverGroupId === groupId) {
      setDragOverGroupId(null);
    }
  };

  const handleDrop = (e: React.DragEvent, targetGroupId: string) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('text/plain') || draggedTaskId;
    setDraggedTaskId(null);
    setDragOverGroupId(null);

    if (!taskId) return;

    if (groupBy === 'sprint') {
      const newSprintId = targetGroupId === 'backlog' ? null : targetGroupId;
      onUpdateTask(taskId, { sprintId: newSprintId as any });
    } else if (groupBy === 'epic') {
      const newEpicId = targetGroupId === 'no-epic' ? null : targetGroupId;
      onUpdateTask(taskId, { epicId: newEpicId as any });
    } else if (groupBy === 'status') {
      onUpdateTask(taskId, { status: targetGroupId as TaskStatus });
    }
  };

  const toggleTaskSelection = (taskId: string) => {
    setSelectedTasks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setTimeout(() => setSelectedTask(null), 300);
  };

  const getGroupName = (groupId: string) => {
    if (groupBy === 'sprint') {
      if (groupId === 'backlog') return 'Backlog';
      const sprint = sprints.find((s) => String(s.id) === String(groupId));
      return sprint?.name || 'Unknown Sprint';
    } else if (groupBy === 'epic') {
      if (groupId === 'no-epic') return 'No Epic';
      const epic = epics.find((e) => String(e.id) === String(groupId));
      return epic?.name || 'Unknown Epic';
    } else if (groupBy === 'status') {
      return groupId.replace('_', ' ').toUpperCase();
    }
    return 'All Tasks';
  };

  const getGroupColor = (groupId: string) => {
    if (groupBy === 'epic') {
      const epic = epics.find((e) => e.id === groupId);
      return epic?.color || '#6c757d';
    }
    return '#4A90E2';
  };

  const getStoryPointSum = (taskList: Task[]) => {
    return taskList.reduce((sum, task) => sum + (task.storyPoints || 0), 0);
  };

  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case TaskPriority.URGENT:
        return 'error';
      case TaskPriority.HIGH:
        return 'warning';
      case TaskPriority.MEDIUM:
        return 'info';
      case TaskPriority.LOW:
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.TODO:
        return '#6c757d';
      case TaskStatus.IN_PROGRESS:
        return '#4A90E2';
      case TaskStatus.REVIEW:
        return '#f39c12';
      case TaskStatus.TESTING:
        return '#9b59b6';
      case TaskStatus.DONE:
        return '#27ae60';
      case TaskStatus.BLOCKED:
        return '#e74c3c';
      default:
        return '#6c757d';
    }
  };

  const getTypeColor = (type?: TaskType) => {
    switch (type) {
      case TaskType.FEATURE:
        return '#9b59b6';
      case TaskType.BUG:
        return '#e74c3c';
      case TaskType.QUALITY:
        return '#27ae60';
      case TaskType.INFRASTRUCTURE:
        return '#6c757d';
      case TaskType.RESEARCH:
        return '#4A90E2';
      case TaskType.DOCUMENTATION:
        return '#f39c12';
      default:
        return '#6c757d';
    }
  };

  /** Returns the sprint status if the given groupId belongs to a sprint */
  const getSprintStatus = (groupId: string): 'ACTIVE' | 'COMPLETED' | 'NOT_STARTED' | null => {
    if (groupBy !== 'sprint' || groupId === 'backlog') return null;
    const sprint = sprints.find((s) => String(s.id) === String(groupId));
    return (sprint?.status as any) || null;
  };

  const renderTaskRow = (task: Task, groupId: string, isReadOnly = false) => (
    <TableRow
      key={task.id}
      draggable={!isReadOnly}
      onDragStart={!isReadOnly ? (e) => handleDragStart(e, task.id) : undefined}
      onDragEnd={!isReadOnly ? handleDragEnd : undefined}
      onDragOver={!isReadOnly ? (e) => handleDragOver(e, groupId) : undefined}
      onDragLeave={!isReadOnly ? (e) => handleDragLeave(e, groupId) : undefined}
      onDrop={!isReadOnly ? (e) => handleDrop(e, groupId) : undefined}
      className={`table-board__task-row${isReadOnly ? ' table-board__task-row--readonly' : ''}`}
      hover={!isReadOnly}
      onClick={() => handleTaskClick(task)}
      sx={{
        cursor: isReadOnly ? 'default' : 'grab',
        opacity: draggedTaskId === task.id ? 0.4 : 1,
        backgroundColor:
          isReadOnly
            ? 'rgba(0,0,0,0.02)'
            : dragOverGroupId === groupId && draggedTaskId !== task.id
            ? alpha('#0073ea', 0.08)
            : undefined,
        transition: 'opacity 0.15s ease, background-color 0.15s ease',
        '&:active': { cursor: isReadOnly ? 'default' : 'grabbing' },
      }}
    >
      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {!isReadOnly && <DragIndicatorIcon sx={{ color: '#9ca3af', fontSize: 18, mr: 0.5, cursor: 'grab' }} />}
          {isReadOnly
            ? <LockOutlined sx={{ fontSize: 16, color: '#9ca3af', ml: 0.5 }} />
            : <Checkbox checked={selectedTasks.has(task.id)} onChange={() => toggleTaskSelection(task.id)} />
          }
        </Box>
      </TableCell>
      <TableCell>
        <Box className="table-board__task-title">
          <Typography variant="body2" fontWeight={500}>
            {task.title}
          </Typography>
        </Box>
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        {isReadOnly ? (
          // Read-only avatar for completed sprints
          task.assigneeId ? (() => {
            const assigneeObj = (task as any).assignee || boardMembers.find((m) => String(m.id) === String(task.assigneeId));
            const assigneeName = assigneeObj?.fullName || assigneeObj?.userName || `User #${task.assigneeId}`;
            const initials = assigneeName
              ? assigneeName
                  .trim()
                  .split(' ')
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((n: string) => n.charAt(0).toUpperCase())
                  .join('')
              : '?';

            return (
              <Tooltip title={assigneeName}>
                <Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, bgcolor: '#0073ea' }}>
                  {initials}
                </Avatar>
              </Tooltip>
            );
          })() : (
            <Avatar sx={{ width: 28, height: 28, bgcolor: 'rgba(0,0,0,0.06)', color: '#9ca3af' }}>
              <Person sx={{ fontSize: 16 }} />
            </Avatar>
          )
        ) : (
          // Interactive Select for assignees (Backlog & Active Sprints)
          <Select
            value={task.assigneeId ? String(task.assigneeId) : ''}
            size="small"
            displayEmpty
            variant="standard"
            disableUnderline
            onChange={(e) => {
              const val = e.target.value;
              onUpdateTask(task.id, { assigneeId: val ? val : null });
            }}
            sx={{
              minWidth: 36,
              '& .MuiSelect-select': {
                py: 0.25,
                px: 0.5,
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiSelect-icon': {
                display: 'none',
              },
              '&:hover .MuiSelect-icon': {
                display: 'block',
              },
            }}
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Tooltip title="Unassigned (Click to assign)">
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: 'rgba(0,0,0,0.05)',
                        color: '#64748b',
                        fontSize: 13,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(0,115,234,0.1)', color: '#0073ea' },
                      }}
                    >
                      <Person sx={{ fontSize: 16 }} />
                    </Avatar>
                  </Tooltip>
                );
              }

              const assigneeObj = (task as any).assignee || boardMembers.find((m) => String(m.id) === String(selected));
              const assigneeName = assigneeObj?.fullName || assigneeObj?.userName || `User #${selected}`;
              const initials = assigneeName
                ? assigneeName
                    .trim()
                    .split(' ')
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((n: string) => n.charAt(0).toUpperCase())
                    .join('')
                : '?';

              const colors = [
                '#5b6af0', '#0073ea', '#22c58c', '#f5b942',
                '#f0576b', '#8b7fd6', '#fb8332', '#00bcd4',
              ];
              const colorIdx = String(selected).charCodeAt(0) % colors.length;

              return (
                <Tooltip title={`Assigned to ${assigneeName} (Click to change)`}>
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: colors[colorIdx],
                      color: '#fff',
                      border: '2px solid rgba(255,255,255,0.9)',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                      cursor: 'pointer',
                      transition: 'transform 0.15s ease',
                      '&:hover': { transform: 'scale(1.1)' },
                    }}
                  >
                    {initials}
                  </Avatar>
                </Tooltip>
              );
            }}
          >
            <MenuItem value="">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ width: 24, height: 24, bgcolor: 'rgba(0,0,0,0.08)', color: '#666', fontSize: 12 }}>
                  <Person sx={{ fontSize: 14 }} />
                </Avatar>
                <Typography variant="body2">Unassigned</Typography>
              </Box>
            </MenuItem>
            {boardMembers.map((member) => {
              const memberName = member.fullName || member.userName;
              const memberInitials = memberName
                ? memberName
                    .trim()
                    .split(' ')
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((n: string) => n.charAt(0).toUpperCase())
                    .join('')
                : '?';

              return (
                <MenuItem key={member.id} value={String(member.id)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: 11, fontWeight: 700, bgcolor: '#0073ea' }}>
                      {memberInitials}
                    </Avatar>
                    <Typography variant="body2">{memberName}</Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        )}
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        {isReadOnly ? (
          // Completed sprint: show status as a non-interactive chip
          <Chip
            label={task.status.replace('_', ' ').toUpperCase()}
            size="small"
            sx={{
              backgroundColor: getStatusColor(task.status),
              color: 'white',
              fontWeight: 500,
              fontSize: '0.775rem',
              minWidth: 120,
              cursor: 'default',
            }}
          />
        ) : (
          <Select
            value={task.status}
            size="small"
            onChange={(e) => onUpdateTask(task.id, { status: e.target.value as TaskStatus })}
            sx={{
              minWidth: 140,
              '& .MuiSelect-select': {
                py: 0.5,
                backgroundColor: getStatusColor(task.status),
                color: 'white',
                fontWeight: 500,
                fontSize: '0.875rem',
              },
            }}
          >
            {Object.values(TaskStatus).map((status) => (
              <MenuItem key={status} value={status}>
                {status.replace('_', ' ').toUpperCase()}
              </MenuItem>
            ))}
          </Select>
        )}
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        {task.type && (
          <Chip
            label={task.type.toUpperCase()}
            size="small"
            sx={{
              backgroundColor: getTypeColor(task.type),
              color: 'white',
              fontWeight: 500,
            }}
          />
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {task.key || task.id.substring(0, 8)}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Chip
          label={task.storyPoints || 0}
          size="small"
          variant="outlined"
          sx={{ minWidth: 40 }}
        />
      </TableCell>
      <TableCell>
        {task.epicId && (
          <Chip
            label={epics.find((e) => e.id === task.epicId)?.name || 'Epic'}
            size="small"
            sx={{
              backgroundColor: epics.find((e) => e.id === task.epicId)?.color || '#6c757d',
              color: 'white',
            }}
          />
        )}
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        {task.priority && (
          <Chip
            icon={<Flag />}
            label={task.priority.toUpperCase()}
            size="small"
            color={getPriorityColor(task.priority)}
          />
        )}
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <IconButton size="small">
          <MoreVert fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );

  const renderColGroup = () => (
    <colgroup>
      <col style={{ width: '75px' }} />
      <col style={{ width: 'auto', minWidth: '150px' }} />
      <col style={{ width: '60px' }} />
      <col style={{ width: '160px' }} />
      <col style={{ width: '120px' }} />
      <col style={{ width: '90px' }} />
      <col style={{ width: '60px' }} />
      <col style={{ width: '120px' }} />
      <col style={{ width: '120px' }} />
      <col style={{ width: '60px' }} />
    </colgroup>
  );

  return (
    <Box className="table-board">
      <TableContainer component={Paper} className="table-board__container">
        <Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
          {renderColGroup()}
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox />
              </TableCell>
              <TableCell>Task</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Task ID</TableCell>
              <TableCell align="center">SP</TableCell>
              <TableCell>Epic</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(groupedTasks)
              .sort(([aId], [bId]) => {
                if (aId === 'backlog') return -1;
                if (bId === 'backlog') return 1;
                return 0;
              })
              .map(([groupId, groupTasks]) => {
              const isExpanded = !collapsedGroups.has(groupId);
              const storyPointSum = getStoryPointSum(groupTasks);
              const isDropHover = dragOverGroupId === groupId;
              const sprintStatus = getSprintStatus(groupId);
              const completedSprint = sprintStatus === 'COMPLETED';
              const activeSprint = sprintStatus === 'ACTIVE';

              return (
                <React.Fragment key={groupId}>
                  {/* Group Header */}
                  {groupBy !== 'none' && (
                    <TableRow
                      className="table-board__group-header"
                      onDragOver={(e) => handleDragOver(e, groupId)}
                      onDragLeave={(e) => handleDragLeave(e, groupId)}
                      onDrop={(e) => handleDrop(e, groupId)}
                      sx={{
                        backgroundColor: isDropHover ? alpha('#0073ea', 0.15) : undefined,
                        outline: isDropHover ? '2px dashed #0073ea' : undefined,
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <TableCell colSpan={10}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            py: 1,
                            cursor: 'pointer',
                          }}
                          onClick={() => toggleGroup(groupId)}
                        >
                          <IconButton size="small">
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                          <Box
                            sx={{
                              width: 4,
                              height: 24,
                              backgroundColor: getGroupColor(groupId),
                              borderRadius: 1,
                              mr: 2,
                            }}
                          />
                          <Typography variant="subtitle1" fontWeight={600}>
                            {getGroupName(groupId)}
                          </Typography>
                          <Chip
                            label={`${groupTasks.length} tasks`}
                            size="small"
                            sx={{ ml: 2 }}
                          />
                          <Chip
                            label={`${storyPointSum} SP`}
                            size="small"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                          {isDropHover && !completedSprint && (
                            <Chip
                              label="Drop here to move task"
                              size="small"
                              color="primary"
                              sx={{ ml: 2, fontWeight: 600 }}
                            />
                          )}
                          {activeSprint && (
                            <Chip
                              label="ACTIVE"
                              size="small"
                              sx={{
                                ml: 2,
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                backgroundColor: 'rgba(0, 115, 234, 0.12)',
                                color: '#0073ea',
                                border: '1px solid rgba(0, 115, 234, 0.3)',
                              }}
                            />
                          )}
                          {completedSprint && (
                            <Chip
                              icon={<LockOutlined sx={{ fontSize: '14px !important' }} />}
                              label="COMPLETED"
                              size="small"
                              sx={{
                                ml: 2,
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                backgroundColor: 'rgba(34,197,140,0.12)',
                                color: '#22c58c',
                                border: '1px solid rgba(34,197,140,0.3)',
                              }}
                            />
                          )}
                          <Box sx={{ flexGrow: 1 }} />
                          {canCreateTask && !completedSprint && (
                            <Button
                              size="small"
                              startIcon={<Add />}
                              onClick={(e) => {
                                e.stopPropagation();
                                onCreateTask(groupBy === 'sprint' && groupId !== 'backlog' ? groupId : undefined);
                              }}
                            >
                              Add Task
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Group Tasks */}
                  {(groupBy === 'none' || isExpanded) && (
                    <>
                      {groupTasks.map((task) => renderTaskRow(task, groupId, completedSprint))}
                      
                      {groupTasks.length === 0 && (
                        <TableRow
                          onDragOver={(e) => handleDragOver(e, groupId)}
                          onDragLeave={(e) => handleDragLeave(e, groupId)}
                          onDrop={(e) => handleDrop(e, groupId)}
                          sx={{
                            backgroundColor: isDropHover ? alpha('#0073ea', 0.08) : undefined,
                          }}
                        >
                          <TableCell colSpan={10} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                              No tasks in this group (Drag & drop tasks here)
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskDetail
          // selectedTask only tracks WHICH task is open (and survives the
          // fade-out after close) - re-resolve it from the live `tasks`
          // prop on every render so an update made inside the dialog (e.g.
          // reassigning) is reflected immediately instead of the dialog
          // showing a stale snapshot captured at click-time.
          task={tasks.find((t) => t.id === selectedTask.id) || selectedTask}
          open={detailOpen}
          onClose={handleCloseDetail}
          onUpdate={onUpdateTask}
        />
      )}
    </Box>
  );
};

export default TableBoardView;
